import React, { useCallback, useRef, useState } from 'react'
import { Box, Button, Typography, Chip } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

const DEBOUNCE_MS = 2000

interface DataAgeIndicatorProps {
  fetchedAt: Date | null
  dataAgeSeconds: number
  isStale: boolean
  loading: boolean
  onRefresh: () => void
}

export const DataAgeIndicator: React.FC<DataAgeIndicatorProps> = ({
  fetchedAt,
  dataAgeSeconds,
  isStale,
  loading,
  onRefresh,
}) => {
  const [isDebouncing, setIsDebouncing] = useState(false)
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleRefresh = useCallback(() => {
    if (isDebouncing || loading) return

    onRefresh()
    setIsDebouncing(true)

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setIsDebouncing(false)
    }, DEBOUNCE_MS)
  }, [isDebouncing, loading, onRefresh])
  const formatAge = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''} ago`
    }
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
    }
    const hours = Math.floor(minutes / 60)
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  }

  if (!fetchedAt) {
    return null
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Last updated: {formatAge(dataAgeSeconds)}
      </Typography>

      {isStale && (
        <Chip label="Data may be outdated" color="warning" size="small" variant="outlined" />
      )}

      {isStale && (
        <Button
          variant="contained"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading || isDebouncing}
          color="warning"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      )}
    </Box>
  )
}
